import { STRETCH_COUNT, FREE_STRETCH_COUNT } from '../lib/product.js'
import { MONTHLY_PRICE, YEARLY_PRICE } from '../lib/facts.js'
import { ALL_APPS, SOURCES as S } from './competitors.js'

// One entry per comparison page. Each page needs its own analysis, not a
// reordered table, and must concede at least one category to a competitor.
// Stop at six pages.
//
// `verdict` is used verbatim as both the meta description and an <h2>, so it
// must stay hedged and multi-winner.

export const COMPARE_PUBLISHED = '2026-10-07'

export const COMPARE_PAGES = [
    {
        slug: 'stretchly-alternatives',
        title: 'Stretchly Alternatives (2026): 6 Break Reminder Apps Compared - Keybreak',
        h1: 'Stretchly alternatives: 6 break reminder apps compared',
        verdict:
            'If you want to stay free and open source, BreakTimer is the closest like-for-like swap and Workrave the stricter one, Time Out is the friendliest free option on a Mac, Restier adds meeting detection for a small yearly fee, and Keybreak is the pick if you want each stretch demonstrated rather than described.',
        apps: ['stretchly', 'breakTimer', 'workrave', 'timeOut', 'restier', 'keybreak'],
        intro: [
            {
                text: 'Stretchly is the default recommendation for a free break reminder, and for good reason: it is open source, runs on macOS, Windows and Linux, pauses itself when you go idle or switch on Do Not Disturb, and ships in 54 languages. Most people looking for an alternative are not unhappy with the timer. They want something different to happen once the break starts.',
                sources: [S.stretchlyReadme, S.stretchlyAbout, S.stretchlyLocales],
            },
        ],
        analysis: [
            {
                heading: 'What a Stretchly break actually gives you',
                body: [
                    {
                        text: 'Stretchly ships 51 mini-break ideas and 36 long-break ideas, each a single line of text. Many are specific (“Slowly tilt head to side and hold for 5-10 seconds.”), so it is not fair to say it leaves you guessing. What it does not do is show you the movement, time the hold for you, or know which movements you should skip.',
                        sources: [S.stretchlyIdeas],
                    },
                    {
                        text: 'That is the real dividing line between the alternatives below. BreakTimer and Time Out sit on the same side as Stretchly: they interrupt you and leave the break to you. Workrave adds 16 illustrated exercises. Keybreak films every one of its stretches with a real person and counts the hold down on screen.',
                        sources: [S.breakTimerReadme, S.timeOut, S.workraveExercises, S.keybreakPricing],
                    },
                ],
            },
            {
                heading: 'If what you like about Stretchly is that it is free',
                body: [
                    {
                        text: 'Three of the alternatives cost nothing at all: BreakTimer and Workrave are free software, and Time Out’s core features are always free. Keybreak’s free plan keeps break reminders and ' +
                            `${FREE_STRETCH_COUNT} of its ${STRETCH_COUNT} stretches; the rest are in Keybreak Plus. Restier is the only one here with no free tier. It has a 14-day trial.`,
                        sources: [S.breakTimerReadme, S.workraveDownload, S.timeOut, S.keybreakPricing, S.restierPricing],
                    },
                    {
                        text: 'If open source matters to you in itself, the honest answer is that Keybreak, Time Out and Restier are not, and Stretchly, Workrave and BreakTimer are.',
                        sources: [S.stretchlyReadme, S.workraveDownload, S.breakTimerReadme],
                    },
                ],
            },
        ],
        choose: {
            stretchly: 'you want one free, open-source app that behaves the same on Mac, Windows and Linux, in your own language.',
            breakTimer: 'you like Stretchly’s approach and just want a simpler, more configurable timer.',
            workrave: 'you are on Windows or Linux and want stricter enforcement, with illustrated exercises.',
            timeOut: 'you only use a Mac and want something that feels native and costs nothing.',
            restier: 'you want meeting detection and a Linux build, and are happy to pay a small yearly or one-off fee.',
            keybreak: 'you want to be shown exactly which stretch to do and for how long, with movements you need to avoid left out.',
        },
        fallsShortExtra: [
            {
                text: 'It is available in 7 languages; Stretchly ships 54.',
                sources: [S.keybreakLanguages, S.stretchlyLocales],
            },
            { text: 'It is not open source.', sources: [] },
        ],
    },
    {
        slug: 'workrave-alternatives',
        title: 'Workrave Alternatives for Mac and Windows (2026) - Keybreak',
        h1: 'Workrave alternatives for Mac and Windows',
        verdict:
            'On a Mac, Stretchly is the closest free replacement for Workrave and Time Out the most Mac-native; on Windows, Workrave itself is still hard to beat for free; Restier covers Linux too for a small yearly fee; and Keybreak is the pick if you want the exercises demonstrated by a real person instead of drawn.',
        apps: ['workrave', 'stretchly', 'timeOut', 'breakTimer', 'restier', 'keybreak'],
        intro: [
            {
                text: 'Workrave is actively maintained (version 1.11.4 shipped on 25 September 2026), and it is free software built around micro-breaks, rest breaks and a daily usage limit. The most common reason to look elsewhere is not quality. It is that Workrave’s stable releases are for Windows and Linux only, and the macOS port is a development build the project itself says has not been thoroughly tested.',
                sources: [S.workraveDownload, S.workrave],
            },
        ],
        analysis: [
            {
                heading: 'If you are moving to a Mac',
                body: [
                    {
                        text: 'Every other app in the table has a supported macOS build. Stretchly is the nearest in spirit: free, open source and cross-platform. Time Out is Mac-only and the most at home there, with the core features free and versions on the Mac App Store and Setapp. Note that Time Out 3.1 needs macOS 26; older Macs get version 2.9.7.',
                        sources: [S.stretchlyReadme, S.timeOut],
                    },
                ],
            },
            {
                heading: 'If you are staying on Windows',
                body: [
                    {
                        text: 'Workrave’s three-layer model (a micro-break every few minutes, a longer rest break, and a daily limit that tells you to stop for the day) is the strictest setup here. None of the alternatives is a reason to leave it if strict enforcement is what you want.',
                        sources: [S.workrave],
                    },
                    {
                        text: 'Where they differ is the exercise itself. Workrave shows 16 exercises as drawings. Keybreak has ' +
                            `${STRETCH_COUNT} stretches filmed with a real person, with the hold timed on screen, and Keybreak Plus lets you exclude movements you have been told to avoid. Keybreak’s micro breaks, part of Keybreak Plus, come every 10 to 15 minutes rather than every few.`,
                        sources: [S.workraveExercises, S.keybreakPricing],
                    },
                ],
            },
        ],
        choose: {
            workrave: 'you are on Windows or Linux and want the strictest free break enforcement, including a daily limit.',
            stretchly: 'you need a free replacement that runs properly on a Mac as well as Windows and Linux.',
            timeOut: 'you have moved to a Mac and want the most Mac-native free option.',
            breakTimer: 'you want a lighter, simpler free timer than Workrave on any platform.',
            restier: 'you need Mac, Windows and Linux under one paid licence, with meeting detection.',
            keybreak: 'you want Workrave’s idea of exercise breaks, demonstrated on video, with movements you exclude left out, on Mac and Windows.',
        },
        fallsShortExtra: [
            { text: 'It has no daily usage limit like Workrave’s.', sources: [S.workrave] },
        ],
    },
    {
        slug: 'time-out-alternatives',
        title: 'Time Out Alternatives for Mac (2026): 6 Apps Compared - Keybreak',
        h1: 'Time Out alternatives for Mac',
        verdict:
            'Time Out remains the most Mac-native free choice, Stretchly is the best free pick if you also use Windows or Linux, Restier adds meeting detection and a lifetime licence, and Keybreak is the one that turns each break into a demonstrated stretch on both Mac and Windows.',
        apps: ['timeOut', 'stretchly', 'restier', 'breakTimer', 'workrave', 'keybreak'],
        intro: [
            {
                text: 'Time Out by Dejal is a Mac break timer whose core features are always free. It is on the Mac App Store and Setapp as well as Dejal’s own site, and its optional Supporter purchases do not renew automatically. There are three common reasons to look at something else.',
                sources: [S.timeOut],
            },
        ],
        analysis: [
            {
                heading: 'You also use Windows or Linux',
                body: [
                    {
                        text: 'Time Out is Mac-only. Stretchly, BreakTimer and Restier run on macOS, Windows and Linux; Keybreak runs on macOS and Windows; Workrave’s stable builds are Windows and Linux, with only a development build for Mac.',
                        sources: [S.timeOut, S.stretchlyReadme, S.breakTimer, S.restierPricing, S.keybreakPricing, S.workraveDownload],
                    },
                ],
            },
            {
                heading: 'Your Mac is older than macOS 26',
                body: [
                    {
                        text: 'Time Out 3.1 requires macOS 26. Dejal still offers version 2.9.7 for macOS 10.15 and later, so this is a reason to check which version you get rather than a reason to switch. Restier supports macOS 11 and later, and Stretchly macOS 13 and later.',
                        sources: [S.timeOut, S.restierPricing, S.stretchlyReadme],
                    },
                ],
            },
            {
                heading: 'You want the break to tell you what to do',
                body: [
                    {
                        text: 'When a Time Out break starts, the screen slowly dims and a break theme appears: text, images, a web page or a video of your choosing. That is ideal for a pause. It does not pick a stretch for you. Keybreak does: each break is one of ' +
                            `${STRETCH_COUNT} stretches filmed with a real person, with the hold timed on screen, chosen around the focus areas you set and the movements you exclude.`,
                        sources: [S.timeOut, S.keybreakPricing],
                    },
                ],
            },
        ],
        choose: {
            timeOut: 'you only use a Mac, want something free that feels native, and like choosing your own break theme.',
            stretchly: 'you want a free app that works the same on your Mac and on Windows or Linux machines.',
            restier: 'you want meeting detection or a one-off lifetime licence, on any desktop OS.',
            breakTimer: 'you want a simple free timer with a choice of notification or fullscreen break.',
            workrave: 'you are mainly on Windows or Linux; on a Mac it is a development build only.',
            keybreak: 'you want every break to be a demonstrated stretch, on Mac and Windows, with a free plan.',
        },
        fallsShortExtra: [
            { text: 'It is not on the Mac App Store or Setapp; Time Out is on both.', sources: [S.timeOut] },
        ],
    },
    {
        slug: 'best-break-reminder-apps',
        title: 'Best Break Reminder Apps for Mac and Windows (2026) - Keybreak',
        h1: 'The best break reminder apps for Mac and Windows in 2026',
        verdict:
            'Stretchly is the best free open-source pick, Workrave is the strictest RSI-focused option on Windows and Linux, Time Out is the easiest Mac-native timer to try first, and Keybreak is the one that actually demonstrates the stretch and leaves out movements you choose to avoid.',
        apps: ALL_APPS,
        intro: [
            {
                text: 'This list is written by the maker of Keybreak, which is one of the six apps in it. Each claim links to the vendor’s own page so you can check it. Rather than ranking them, it is more useful to ask three questions, because the answers rule most of them out quickly.',
                sources: [],
            },
        ],
        analysis: [
            {
                heading: '1. Which computers do you use?',
                body: [
                    {
                        text: 'Mac only: Time Out is the most native choice. Mac and Windows: Stretchly, BreakTimer, Restier and Keybreak. Linux as well: Stretchly, BreakTimer, Workrave and Restier, not Keybreak or Time Out.',
                        sources: [S.timeOut, S.stretchlyReadme, S.breakTimer, S.restierPricing, S.keybreakPricing, S.workraveDownload],
                    },
                ],
            },
            {
                heading: '2. Will you pay?',
                body: [
                    {
                        text: 'Stretchly, Workrave and BreakTimer are free outright. Time Out’s core features are always free, with optional Supporter purchases. Keybreak is free with ' +
                            `${FREE_STRETCH_COUNT} of its ${STRETCH_COUNT} stretches, and Keybreak Plus is ${MONTHLY_PRICE} a month or ${YEARLY_PRICE} a year. Restier has a 14-day trial and then costs from €7.99 a year or €19.99 once, the cheapest paid option here.`,
                        sources: [S.stretchlyAbout, S.workraveDownload, S.breakTimerReadme, S.timeOut, S.keybreakPricing, S.restierPricing],
                    },
                ],
            },
            {
                heading: '3. What should happen when the break starts?',
                body: [
                    {
                        text: 'This is where the apps differ most. BreakTimer shows a notification or a fullscreen window with your own message. Time Out dims the screen and shows a theme. Stretchly suggests a one-line idea in text. Restier shows visual resets, breathing prompts and posture cues. Workrave shows 16 drawn exercises. Keybreak plays a filmed stretch with the hold timed on screen, and leaves out movements you have excluded.',
                        sources: [S.breakTimerReadme, S.timeOut, S.stretchlyIdeas, S.restier, S.workraveExercises, S.keybreakPricing],
                    },
                ],
            },
            {
                heading: 'Meetings and idle time',
                body: [
                    {
                        text: 'All six pause or reset when you step away, in different ways. Restier says its meeting detection avoids interrupting calls; Stretchly pauses while Do Not Disturb is on. Keybreak does neither. It relies on you not typing during a call.',
                        sources: [S.restier, S.stretchlyAbout, S.timeOut, S.breakTimerReadme, S.workrave, S.keybreakApp],
                    },
                ],
            },
        ],
        choose: {
            keybreak: 'you want each break to be a demonstrated stretch, leaving out movements you need to avoid.',
            stretchly: 'you want the best free, open-source all-rounder on any desktop OS.',
            workrave: 'you want strict enforcement, with a daily limit, on Windows or Linux.',
            timeOut: 'you only use a Mac and want the quickest free thing to try.',
            restier: 'you want the cheapest paid option, meeting detection, Linux, or a lifetime licence.',
            breakTimer: 'you want a minimal free timer and will decide what to do in the break yourself.',
        },
        fallsShortExtra: [],
    },
    {
        slug: 'best-free-break-reminder-app',
        title: "Best Free Break Reminder Apps (2026): What 'Free' Actually Means - Keybreak",
        h1: "Best free break reminder apps, and what each one's 'free' actually means",
        verdict:
            "Stretchly, Workrave and BreakTimer are free outright and open source, Time Out's core is always free on a Mac, Keybreak's free plan gives personal users break reminders and 8 of its 37 stretches, and Restier has no free tier, only a 14-day trial.",
        apps: ALL_APPS,
        intro: [
            {
                text: '“Free” covers four quite different deals among break reminder apps. Knowing which one you are getting matters more than the feature list, because it decides whether the app is still free in a month.',
                sources: [],
            },
        ],
        analysis: [
            {
                heading: 'Free and open source',
                body: [
                    {
                        text: 'Stretchly (BSD-2-Clause), Workrave (GPL) and BreakTimer (GPL-3.0) are free with no paid tier at all. Stretchly takes optional donations. If free means “nothing will ever be held back”, these are the three.',
                        sources: [S.stretchlyReadme, S.stretchlyAbout, S.workraveDownload, S.breakTimerReadme],
                    },
                ],
            },
            {
                heading: 'Free core, optional support',
                body: [
                    {
                        text: 'Time Out’s core features are always free. Its Supporter purchases (from $4.99 for three months to $59.99 for life) do not renew automatically.',
                        sources: [S.timeOut],
                    },
                ],
            },
            {
                heading: 'Free plan, paid upgrade',
                body: [
                    {
                        text: `Keybreak’s free plan includes activity-aware break reminders and ${FREE_STRETCH_COUNT} of the ${STRETCH_COUNT} guided stretches, on Mac and Windows, with no account. Keybreak Plus unlocks the other ${STRETCH_COUNT - FREE_STRETCH_COUNT} stretches, focus areas, injury exclusions and micro breaks for ${MONTHLY_PRICE} a month or ${YEARLY_PRICE} a year. Both are licensed for personal, non-commercial use; a business using Keybreak needs a Teams subscription.`,
                        sources: [S.keybreakPricing],
                    },
                ],
            },
            {
                heading: 'Free trial',
                body: [
                    {
                        text: 'Restier has no free tier. Every feature is free for 14 days with no card, and then it costs from €7.99 a year or €19.99 for a lifetime licence on one device, with a 14-day money-back guarantee.',
                        sources: [S.restierPricing],
                    },
                ],
            },
        ],
        choose: {
            stretchly: 'you want the most complete app that will always be entirely free.',
            workrave: 'you want free, strict break enforcement on Windows or Linux.',
            breakTimer: 'you want a free, minimal timer with nothing to upgrade to.',
            timeOut: 'you are on a Mac and want a free core with the option to support the developer.',
            keybreak: 'you want guided stretches for free and may pay later for the full library.',
            restier: 'you will pay a small amount after a trial for meeting detection and a Linux build.',
        },
        fallsShortExtra: [
            {
                text: `Its free plan holds back ${STRETCH_COUNT - FREE_STRETCH_COUNT} of the ${STRETCH_COUNT} stretches, focus areas and injury exclusions; the open-source apps hold back nothing.`,
                sources: [S.keybreakPricing],
            },
            {
                text: 'Its free plan, like Keybreak Plus, is for personal, non-commercial use; using Keybreak for a business needs a paid Teams subscription.',
                sources: [S.keybreakEula],
            },
        ],
    },
    {
        slug: 'keybreak-vs-restier',
        title: 'Keybreak vs Restier (2026): Free Plan vs 14-Day Trial - Keybreak',
        h1: 'Keybreak vs Restier',
        verdict:
            `Keybreak has a free plan rather than a 14-day clock, and demonstrates each of its ${STRETCH_COUNT} stretches on video while leaving out movements you choose to avoid; Restier costs less, adds a one-off lifetime licence, a Linux build and meeting detection.`,
        apps: ['keybreak', 'restier'],
        intro: [
            {
                text: 'Keybreak and Restier are both paid break reminder apps for desktop computers, and both are aimed at people who sit at a desk all day. They make different trade-offs on price, platforms and what a break contains.',
                sources: [S.keybreakPricing, S.restier],
            },
        ],
        analysis: [
            {
                heading: 'Price',
                body: [
                    {
                        text: 'The two price differently. Restier sells a one-device licence at €7.99 a year or €19.99 once, with 2- and 5-device tiers. Keybreak Plus is ' +
                            `${MONTHLY_PRICE} a month or ${YEARLY_PRICE} a year and has no lifetime option; a licence can be active on up to 3 devices at a time, across Mac and Windows.`,
                        sources: [S.restierPricing, S.keybreakPricing],
                    },
                    {
                        text: `The bigger difference is what happens if you do not pay. Restier is a 14-day trial with no free tier. Keybreak has a free plan, with break reminders and ${FREE_STRETCH_COUNT} stretches.`,
                        sources: [S.restierPricing, S.keybreakPricing],
                    },
                ],
            },
            {
                heading: 'Platforms and meetings',
                body: [
                    {
                        text: 'Restier runs on macOS 11+, Windows 10+ and Linux, and says it detects meetings to avoid interrupting calls and supports full-screen overlays across multiple monitors. Keybreak runs on macOS and Windows, and times breaks from your real keyboard and mouse activity within the hours you set rather than from your calendar.',
                        sources: [S.restierPricing, S.restier, S.keybreakApp],
                    },
                ],
            },
            {
                heading: 'What a break contains',
                body: [
                    {
                        text: `Restier describes its breaks as visual resets, breathing prompts and posture cues. Keybreak’s are ${STRETCH_COUNT} stretches, each filmed with a real person and the hold timed on screen, chosen around focus areas you set and leaving out movements you have excluded.`,
                        sources: [S.restier, S.keybreakPricing],
                    },
                ],
            },
        ],
        choose: {
            restier: 'you want the lowest price, a lifetime licence, Linux, or meeting detection.',
            keybreak: 'you want a free plan, filmed stretches, and injury exclusions, on Mac and Windows.',
        },
        fallsShortExtra: [],
    },
]

export const comparePage = (slug) => COMPARE_PAGES.find((page) => page.slug === slug)

export const COMPARE_SLUGS = COMPARE_PAGES.map(({ slug }) => slug)
